import { useState } from 'react';
import { useLocalization } from 'hooks/useLocalization';
import { useActivityShopUI } from 'hooks/useActivityShopUI';
import { useBusiness } from 'hooks/useBusiness';
import { Box, TextField, IconButton, InputAdornment, Button, SxProps, List, ListItem, ListItemText, Typography } from '@mui/material';
import { ArrowBackIosNew as ArrowLeftIcon, ArrowForwardIos as ArrowRightIcon, Group as GroupIcon } from '@mui/icons-material';

import { AgeGroupDetails } from "interfaces/BusinessInterfaces";
import { getSingularOrPluralCode } from 'utils/formatUtils';
import GeneralDialog from 'components/shared/GeneralDialog';
import DefaultThemeWrapper from 'components/themes/DefaultThemeWrapper';
import { useUI } from 'hooks/useUI';

interface PaxQtySelectorProps {
	sx?: SxProps;
}

function PaxQtySelector({ sx }: PaxQtySelectorProps) {
	const { getBizConfigVar } = useBusiness();
	const { getStr } = useLocalization();
	const { modifyPax, getPaxQtyOfType } = useActivityShopUI();
	const { isSizeOrLess } = useUI();
	const [changePaxDialogIsOpen, setChangePaxDialogIsOpen] = useState<boolean>(false);
	
	const ageGroupBizConfig = getBizConfigVar('ageGroups');
	const usesAgeGroups = getBizConfigVar('usesAgeGroups');
	const isXsApp = isSizeOrLess('xs');

	const getAgeRangeString = (ageGroupInConfig: AgeGroupDetails) => {
		let str: string = String(ageGroupInConfig.age.min);

		if (ageGroupInConfig.age.max !== 100) {
			str += '-' + ageGroupInConfig.age.max;
		} else {
			str += '+';
		}

		str += ' ' + getStr('years');
		return str;
	}

	return (
		<>
			<Box sx={sx}>
				{(usesAgeGroups || isXsApp) ? (
					<ChangePaxQtyDialogButton onClick={() => {setChangePaxDialogIsOpen(true)}} />
				) : (
					<PaxQtySelectorNoAgeGroups />
				)}
			</Box>
			<DefaultThemeWrapper>
				<GeneralDialog
					isOpen={changePaxDialogIsOpen}
					onClose={() => {setChangePaxDialogIsOpen(false)}}
					title={getStr('Select_passenger_amount')}
					content={
						<List>
							{ageGroupBizConfig.map((ageGroupInConfig, index) => {
								if (ageGroupInConfig.enabled) {
									return (
										<ListItem key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
											<ListItemText
												primary={getStr('AGE_GROUP_TITLE_' + ageGroupInConfig.code)}
												secondary={getAgeRangeString(ageGroupInConfig)}
												sx={{ paddingRight: '20px' }}
											/>
											<div>
												<IconButton onClick={() => {modifyPax(ageGroupInConfig.code, false)}}>
													<ArrowLeftIcon />
												</IconButton>
												<span style={{ width: '40px', display: 'inline-block', textAlign: 'center', fontWeight: 'bold' }}>{getPaxQtyOfType(ageGroupInConfig.code)}</span>
												<IconButton onClick={() => {modifyPax(ageGroupInConfig.code, true)}}>
													<ArrowRightIcon />
												</IconButton>
											</div>
										</ListItem>
									);
								}
								return null;
							})}
						</List>
					}
				/>
			</DefaultThemeWrapper>
		</>
	);
}

function PaxQtySelectorNoAgeGroups() {
	const { getStr } = useLocalization();
	const { modifyPax, getPaxQtyOfType } = useActivityShopUI();
	const { isSizeOrLess } = useUI();
	
	const isXsApp = isSizeOrLess('xs');

	return(
		<>
			<Box display="flex" alignItems="center" className="tt-pax-selector-no-age-groups">
				{!isXsApp && <GroupIcon style={{ marginRight: '15px' }} />}
				<TextField
					label={getStr('Passengers')}
					type="text"
					variant="outlined"
					value={getPaxQtyOfType('adult')}
					sx={{
						width: '130px'
					}}
					InputProps={{
						style: { fontWeight: 'bold', padding: '0 8px' },
						startAdornment: (
							<InputAdornment position="start">
								<IconButton onClick={() => {modifyPax('adult', false)}} aria-label="Decrement pax" sx={{ padding: '8px' }}>
									<ArrowLeftIcon sx={{ fontSize: '18px' }} />
								</IconButton>
							</InputAdornment>
						),
						endAdornment: (
							<InputAdornment position="end">
								<IconButton onClick={() => {modifyPax('adult', true)}} aria-label="Increment pax" sx={{ padding: '8px' }}>
									<ArrowRightIcon sx={{ fontSize: '18px' }} />
								</IconButton>
							</InputAdornment>
						)
					}}
					inputProps={{ style: { textAlign: 'center', paddingTop: '10px', paddingBottom: '10px' } }}
				/>
			</Box>
		</>
	);
}

interface ChangePaxQtyDialogButtonProps {
	onClick: () => void;
}

function ChangePaxQtyDialogButton({ onClick }: ChangePaxQtyDialogButtonProps) {
	const { getStr } = useLocalization();
	const { getTotalPaxQty, paxArr } = useActivityShopUI();
	const { getBizConfigVar } = useBusiness();
	const { isSizeOrLess } = useUI();
	
	const isXsApp = isSizeOrLess('xs');
	const isSmApp = isSizeOrLess('sm');

	return (
		<>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
				{
					isSmApp ? (
						<Button
							variant="contained"
							onClick={onClick}
						>
							<GroupIcon sx={{ marginRight: '10px' }} />
							{getTotalPaxQty(paxArr)}
						</Button>
					) : (
						<>
							<GroupIcon />
							<Typography>{getTotalPaxQty(paxArr)}{!isXsApp && ` ${getStr(getSingularOrPluralCode(getTotalPaxQty(paxArr), 'person', 'people'))}`}</Typography>
							<Button
								variant="contained"
								onClick={onClick}
								sx={{ paddingTop: '8px', paddingBottom: '8px' }}
							>
								{getStr('Change')}
							</Button>
						</>
					)
				}
			</Box>
		</>
	);
}

export default PaxQtySelector;